import { Component } from '@theme/component';

/**
 * A vertical word-scroller for the Booking CTA.
 *
 * The list is clipped by CSS, so the current link sits on the baseline with
 * the next one sliding through the fade below. The list advances up by one
 * line every few seconds and holds still while the pointer is over the
 * scroller or focus is inside it. The component is a progressive
 * enhancement — without JS the list simply shows the first link, and every
 * link stays reachable via the tab order.
 *
 * The loop is a sliding window: the links are topped up to a minimum count
 * with whole-set copies, and each advance slides the list one line up. The
 * link that scrolled out is then moved to the end of the list while the
 * offset resets with the transition suppressed — the list looks identical
 * afterwards, so the loop never shows a seam and the offset never grows past
 * a single line.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} list - The translating list of links.
 *
 * @extends {Component<Refs>}
 */
class BookingScrollerComponent extends Component {
  requiredRefs = ['list'];

  /** How long each link stays on the baseline before advancing, in ms. */
  static #INTERVAL = 2000;

  /** Minimum rendered links, so the window below the baseline never runs dry. */
  static #MIN_LINKS = 5;

  /** Whether the link that scrolled out on the last advance still heads the list. */
  #pendingRecycle = false;

  /** @type {number | undefined} */
  #timer;

  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();

    if (this.refs.list.children.length <= 1) return;

    this.#shuffleLinks();
    this.#topUpLinks();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('pointerenter', () => this.#pause(), { signal });
    this.addEventListener('pointerleave', () => this.#play(), { signal });
    // Hold still while a link has keyboard focus — recycling would move the
    // focused link, and moving an element drops its focus.
    this.addEventListener('focusin', () => this.#pause(), { signal });
    this.addEventListener('focusout', () => this.#play(), { signal });

    this.#play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#pause();
    this.#controller?.abort();
  }

  /** Start advancing on a fixed interval (no-op while running or focused). */
  #play() {
    if (this.#timer != null || this.matches(':focus-within')) return;
    this.#timer = setInterval(() => this.#advance(), BookingScrollerComponent.#INTERVAL);
  }

  /** Stop advancing. */
  #pause() {
    if (this.#timer == null) return;
    clearInterval(this.#timer);
    this.#timer = undefined;
  }

  /**
   * Re-append the links in a random order so each page view cycles through
   * them in a different sequence. Runs before the top-up so the copies match
   * the shuffled order. Without JS the authored order shows.
   */
  #shuffleLinks() {
    const links = Array.from(this.refs.list.children);
    const shuffled = [];

    while (links.length > 0) {
      const j = Math.floor(Math.random() * links.length);
      shuffled.push(...links.splice(j, 1));
    }

    this.refs.list.append(...shuffled);
  }

  /**
   * Duplicate the full set of links until the list holds at least MIN_LINKS,
   * so recycling always has content queued below the baseline. Whole-set
   * copies keep the cyclic order intact (no link ever follows itself).
   * Copies are hidden from assistive tech and the tab order to avoid
   * duplicate stops.
   */
  #topUpLinks() {
    const originals = Array.from(this.refs.list.children);

    while (this.refs.list.children.length < BookingScrollerComponent.#MIN_LINKS) {
      for (const link of originals) {
        const copy = /** @type {HTMLElement} */ (link.cloneNode(true));
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('tabindex', '-1');
        this.refs.list.appendChild(copy);
      }
    }
  }

  #advance() {
    const { list } = this.refs;

    // Recycle the link that scrolled out on the previous advance: move it to
    // the end and reset the offset with the transition suppressed. The list
    // looks identical afterwards, so the loop never shows a seam.
    if (this.#pendingRecycle && list.firstElementChild) {
      list.classList.add('be-booking__list--no-transition');
      list.appendChild(list.firstElementChild);
      this.#setOffset(0);
      // Force reflow so the re-enabled transition doesn't animate the reset.
      void list.offsetHeight;
      list.classList.remove('be-booking__list--no-transition');
    }

    this.#setOffset(1);
    this.#pendingRecycle = true;
  }

  /** @param {number} offset */
  #setOffset(offset) {
    this.refs.list.style.setProperty('--booking-scroll-offset', String(offset));
  }
}

if (!customElements.get('booking-scroller-component')) {
  customElements.define('booking-scroller-component', BookingScrollerComponent);
}
